import { CustomerSearchParams, SiteSearchParams } from '@/types/customers';
import httpClient from './http-client';


export default class CustomerService {
    private baseApiEndpoint = '/api/cust';
    private idToken: string | undefined = '';
    private accessToken: string | undefined = '';

    constructor(idToken?: string | undefined, accessToken?: string | undefined) {
        this.idToken = idToken;
        this.accessToken = accessToken;
        this.baseApiEndpoint = import.meta.env.VITE_BYPASS_PROXY === 'true' 
                                ? '' : this.baseApiEndpoint;
    }

    async getCustomerSitegroups(userId: string): Promise<any> {
        let response
        try {
            response = await httpClient.get(
                `${this.baseApiEndpoint}/customers/?user_id=${userId}`,
                {
                    headers: {
                        // 'X-Id-Authorization': this.idToken,
                        // 'X-Authorization': `Bearer ${this.accessToken}`,
                    },
                }
            );
        } catch (error) {
            console.error('Error fetching details:', error);
            // throw error;
        }
        
        return response?.data || null;
    }

    async getSites(userId: string, type: string, id: string, loadDefault: boolean = false, page: number = 1, limit: number = 100, search?: string, country?: string, province?: string, city?: string,pagination?: string): Promise<any> {
        let response
        try {
            const params: any = {
                user_id: userId,
                type: type,
                id: id,
                page_no: page,
                page_size: limit
            };
            if (pagination) {
                params.pagination = pagination;
            }
            
            if (loadDefault) {
                params.default = true;
            }
            
            // Add search parameter if provided
            if (search?.trim()) {
                params.search = search.trim();
            }
            
            // Add filter parameters if provided
            if (country?.trim()) {
                params.country = country.trim();
            }
            if (province?.trim()) {
                params.province = province.trim();
            }
            if (city?.trim()) {
                params.city = city.trim();
            }
            
            response = await httpClient.get(
                `${this.baseApiEndpoint}/customers/sites`,
                {
                    params,
                    headers: {
                        // 'X-Id-Authorization': this.idToken,
                        // 'X-Authorization': `Bearer ${this.accessToken}`,
                    },
                }
            );
        } catch (error) {
            console.error('Error fetching details:', error);
            // throw error;
        }
        
        return response?.data || null;
    }
     async getLocations(
    userId: string,
    id: number,
    type: string,
    selectionLevel: string, 
    country?: string,
    state?: string,
  ): Promise<any> {
    let response;
    try {
      const params: any = {
        user_id: userId,
        id: id,
        type: type, 
        selection_level: selectionLevel, 
      };


      // Add country parameter if provided (required for states and cities)
      if (country?.trim()) {
        params.country = country.trim();
      }

      // Add state parameter if provided (required for cities)
      if (state?.trim()) {
        params.state = state.trim();
      }

      response = await httpClient.get(
        `${this.baseApiEndpoint}/customers/locations`,
        {
          params,
          headers: {
            // 'X-Id-Authorization': this.idToken,
            // 'X-Authorization': `Bearer ${this.accessToken}`,
          },
        },
      );
    } catch (error) {
      console.error("Error fetching locations:", error);
      return [];
    }

    const data = response?.data;
    if (!data) return [];

    // Use selectionLevel to extract the correct key
    if (selectionLevel === "countries" && data.countries) {
      return data.countries;
    } else if (selectionLevel === "states" && data.states) {
      return data.states;
    } else if (selectionLevel === "cities" && data.cities) {
      return data.cities;
    }

    // Fallback: if data is already an array, return it
    return Array.isArray(data) ? data : [];
  }

     async getSiteGroups(): Promise<any> {
            try {
                const response = await httpClient.get(`${this.baseApiEndpoint}/customers/sitegroups`, {
                    headers: {
                        // 'X-Authorization': `Bearer ${this.accessToken}`,
                        // 'X-Id-Authorization': this.idToken,
                         'Authorization': `Bearer ${this.accessToken}`,
                    },
                });
                return response?.data || null;
            } catch (error) {
                console.error('Error fetching site groups:', error);
                throw error;
            }
        }
    
        /**
         * Fetch customers with pagination and search
         */
        async getCustomers(params: CustomerSearchParams = {}): Promise<any> {
            try {
                const searchParams: any = {
                    page: params.page || 1,
                    limit: params.limit || 100,
                };
    
                if (params.search?.trim()) {
                    searchParams.search = params.search.trim();
                }
    
                const response = await httpClient.get(`${this.baseApiEndpoint}/customers/accounts`, {
                    params: searchParams,
                    headers: {
                        // 'X-Authorization': `Bearer ${this.accessToken}`,
                        // 'X-Id-Authorization': this.idToken,
                         'Authorization': `Bearer ${this.accessToken}`,
                    },
                });
                return response?.data || null;
            } catch (error) {
                console.error('Error fetching customers:', error);
                throw error;
            }
        }
    
        /**
         * Fetch customers by user ID
         */
        async getCustomersByUserId(userId: string): Promise<any> {
            try {
                const response = await httpClient.get(`${this.baseApiEndpoint}/customers`, {
                    params: {
                        user_id: userId,
                        refresh: new Date().getTime(),
                        include_empty_sites: true
                    },
                    headers: {
                        // 'X-Authorization': `Bearer ${this.accessToken}`,
                        // 'X-Id-Authorization': this.idToken,
                         'Authorization': `Bearer ${this.accessToken}`,
                    },
                });
                return response?.data || null;
            } catch (error) {
                console.error('Error fetching customers by user ID:', error);
                throw error;
            }
        }
    
        /**
         * Fetch sites by user ID
         */
        async getSitesByUserId(userId: string, userTableId: number): Promise<any> {
            try {
                const response = await httpClient.get(`${this.baseApiEndpoint}/customers/sites`, {
                    params: {
                        user_id: userId,
                        id: userTableId,
                        type: "user",
                        refresh: new Date().getTime()
                    },
                    headers: {
                        // 'X-Authorization': `Bearer ${this.accessToken}`,
                        // 'X-Id-Authorization': this.idToken,
                         'Authorization': `Bearer ${this.accessToken}`,
                    },
                });
                return response?.data || null;
            } catch (error) {
                console.error('Error fetching sites by user ID:', error);
                throw error;
            }
        }
    
        /**
         * Search sites with pagination
         */
        async searchSites(params: SiteSearchParams = {}): Promise<any> {
            try {
                const searchParams: any = {
                    page: params.page || 1,
                    limit: params.limit || 100,
                };
    
                if (params.search?.trim()) {
                    searchParams.search = params.search.trim();
                }
    
                const response = await httpClient.get(`${this.baseApiEndpoint}/customers/sites/search`, {
                    params: searchParams,
                    headers: {
                        // 'X-Authorization': `Bearer ${this.accessToken}`,
                        // 'X-Id-Authorization': this.idToken,
                         'Authorization': `Bearer ${this.accessToken}`,
                    },
                });
                return response?.data || null;
            } catch (error) {
                console.error('Error searching sites:', error);
                throw error;
            }
        }
    
        /**
         * Fetch customer details and sites by account ID
         */
        async getCustomerDetails(customerAccountId: number): Promise<any> {
            try {
                const response = await httpClient.get(`${this.baseApiEndpoint}/customers/accounts/${customerAccountId}/sites`, {
                    headers: {
                        // 'X-Authorization': `Bearer ${this.accessToken}`,
                        // 'X-Id-Authorization': this.idToken,
                         'Authorization': `Bearer ${this.accessToken}`,
                    },
                });
                return response?.data || null;
            } catch (error) {
                console.error('Error fetching customer details:', error);
                throw error;
            }
        }
        
}


export const fetchCustomersForDialogAPI = async (searchTerm?: string, page: number = 1, limit: number = 100, idToken?: string, accessToken?: string) => {
    const customerService = new CustomerService(idToken, accessToken);
    const params: CustomerSearchParams = { page, limit, search: searchTerm };
    const data = await customerService.getCustomers(params);
    
    const customers = data?.customer_accounts || [];
    const totalCount = data?.totalCount || data?.total || data?.count || 0;
    
    let totalPages = 0;
    if (totalCount > 0) {
        totalPages = Math.ceil(totalCount / limit);
    } else {
        totalPages = customers.length > 0 ? page + (customers.length === limit ? 1 : 0) : page;
    }
    
    return {
        customers,
        totalCount,
        totalPages
    };
};

export const fetchCustomersByUserIdAPI = async (userId: string, idToken?: string, accessToken?: string) => {
    const customerService = new CustomerService(idToken, accessToken);
    return await customerService.getCustomersByUserId(userId);
};

export const fetchSitesByUserIdAPI = async (userId: string, userTableId: number, idToken?: string, accessToken?: string) => {
    const customerService = new CustomerService(idToken, accessToken);
    return await customerService.getSitesByUserId(userId, userTableId);
};

export const fetchSitesForDialogAPI = async (searchTerm?: string, page: number = 1, limit: number = 100, idToken?: string, accessToken?: string) => {
    const customerService = new CustomerService(idToken, accessToken);
    const params: SiteSearchParams = { page, limit, search: searchTerm };
    const data = await customerService.searchSites(params);
    
    if (data?.sites && Array.isArray(data.sites)) {
        return data.sites;
    } else {
        return [];
    }
};

export const fetchLocationsAPI = async (
  userId: string,
  id: number,
  type: "customers" | "site_groups",
  selectionLevel: "countries" | "states" | "cities", // new param
  country?: string,
  state?: string,
  idToken?: string,
  accessToken?: string,
) => {
  const customerService = new CustomerService(idToken, accessToken);
  const data = await customerService.getLocations(
    userId,
    id,
    type,
    selectionLevel,
    country,
    state,
  );

  // Ensure we always return an array
  if (Array.isArray(data)) {
    return data;
  }

  console.warn("fetchLocationsAPI: Expected array but got:", typeof data, data);
  return [];
};
